import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import useModuleAccess from '../hooks/useModuleAccess'

const API_URL = process.env.REACT_APP_API_URL

const ATTRIBUTE_SORT_FIELDS = ['attribute', 'createdAt']
const DEFAULT_SORT_BY = 'createdAt'
const DEFAULT_SORT_ORDER = 'desc'

function normalizeSortBy(value) {
  const field = String(value || '').trim()
  return ATTRIBUTE_SORT_FIELDS.includes(field) ? field : DEFAULT_SORT_BY
}

function normalizeSortOrder(value) {
  const order = String(value || '').trim().toLowerCase()
  return order === 'asc' ? 'asc' : 'desc'
}

function parseApiResponse(raw) {
  let data = raw
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return raw
    }
  }
  if (data?.body != null) {
    if (typeof data.body === 'string') {
      try {
        return JSON.parse(data.body)
      } catch {
        return data.body
      }
    }
    return data.body
  }
  return data
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  if (
    obj.data &&
    typeof obj.data === 'object' &&
    typeof obj.data.data === 'string'
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) }
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const success = responseData.success
  return success === true || success === 'true' || success === 1 || success === '1'
}

function extractAttributesPayload(responseData) {
  const root = responseData
  let dataBlock = root?.data != null ? root.data : root

  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') {
    return { attributes: [], pagination: {}, sortBy: null, sortOrder: null }
  }

  if (Array.isArray(dataBlock)) {
    return {
      attributes: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === 'object'
          ? root.pagination
          : {},
      sortBy: root.sortBy || null,
      sortOrder: root.sortOrder || null
    }
  }

  let list =
    dataBlock.attributes ||
    dataBlock.attributeList ||
    dataBlock.items ||
    root.attributes

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === 'string'
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data
    if (Array.isArray(inner)) {
      list = inner
    } else if (inner && typeof inner === 'object') {
      list = inner.attributes || inner.attributeList || inner.items
    }
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {}

  if (typeof list === 'string') {
    list = parseJsonIfString(list)
  }

  const total =
    typeof pagination.total === 'number'
      ? pagination.total
      : typeof pagination.totalCount === 'number'
        ? pagination.totalCount
        : typeof dataBlock.totalCount === 'number'
          ? dataBlock.totalCount
          : typeof dataBlock.total === 'number'
            ? dataBlock.total
            : undefined

  return {
    attributes: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === 'object' && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
    sortBy: dataBlock.sortBy || root.sortBy || null,
    sortOrder: dataBlock.sortOrder || root.sortOrder || null
  }
}

function extractAttributeDetail(responseData) {
  const root = responseData
  if (!root || typeof root !== 'object') return null
  const data = root.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data
  }
  return null
}

function attributeUserName(user) {
  if (!user) return '-'
  if (typeof user === 'string') return user.trim() || '-'
  return user.name || user.userName || '-'
}

function formatAttributeDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}

function getAttributeRecordId(attribute) {
  return attribute?._id || attribute?.id || ''
}

function getAttributeLabel(attribute) {
  return String(attribute?.attribute || attribute?.attributeName || '').trim()
}

const AttributeDetailCard = ({ label, value, icon }) => (
  <div
    className="h-100"
    style={{
      backgroundColor: '#f8fafb',
      border: '1px solid #e3e8ee',
      borderRadius: '8px',
      padding: '14px 16px'
    }}
  >
    <div
      className="d-flex align-items-center mb-2"
      style={{ color: '#6c757d', fontSize: '12px', fontWeight: 600 }}
    >
      {icon ? <i className={`${icon} mr-2`} style={{ color: '#1199B2' }} /> : null}
      {label}
    </div>
    <div style={{ color: '#212529', fontSize: '14px', fontWeight: 600, wordBreak: 'break-word' }}>
      {value || '-'}
    </div>
  </div>
)

const openBootstrapModal = (selector) => {
  if (window.$) window.$(selector).modal('show')
}

const closeBootstrapModal = (selector) => {
  if (window.$) window.$(selector).modal('hide')
}

const Attribute = () => {
  const { canCreate, canView, canUpdate, canDelete } = useModuleAccess(
    'attribute_management'
  )
  const savedFilters = JSON.parse(sessionStorage.getItem('attributeFilters') || '{}')
  const savedSearchQuery = String(savedFilters.searchQuery || '')
  // If the input was cleared, never keep a stale active search from sessionStorage
  const savedActiveSearchQuery = savedSearchQuery.trim()
    ? String(savedFilters.activeSearchQuery || savedSearchQuery)
    : ''

  const [attributes, setAttributes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState(savedSearchQuery)
  const [activeSearchQuery, setActiveSearchQuery] = useState(savedActiveSearchQuery)
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1)
  const [itemsPerPage, setItemsPerPage] = useState(savedFilters.itemsPerPage || 25)
  const [sortBy, setSortBy] = useState(
    normalizeSortBy(savedFilters.sortBy || DEFAULT_SORT_BY)
  )
  const [sortOrder, setSortOrder] = useState(
    normalizeSortOrder(savedFilters.sortOrder || DEFAULT_SORT_ORDER)
  )

  const [addAttributeName, setAddAttributeName] = useState('')
  const [isCreatingAttribute, setIsCreatingAttribute] = useState(false)

  const [viewAttribute, setViewAttribute] = useState(null)
  const [isLoadingViewAttribute, setIsLoadingViewAttribute] = useState(false)

  const [editAttribute, setEditAttribute] = useState(null)
  const [editAttributeName, setEditAttributeName] = useState('')
  const [isLoadingEditAttribute, setIsLoadingEditAttribute] = useState(false)
  const [isUpdatingAttribute, setIsUpdatingAttribute] = useState(false)

  const [deleteAttributeId, setDeleteAttributeId] = useState(null)
  const [deleteAttributeLabel, setDeleteAttributeLabel] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const fetchAttributes = useCallback(async () => {
    setIsLoading(true)
    try {
      const payload = {
        action: 'get-all-attribute',
        page: currentPage,
        limit: itemsPerPage,
        sortBy: normalizeSortBy(sortBy),
        sortOrder: normalizeSortOrder(sortOrder)
      }

      const trimmedSearch = String(activeSearchQuery || '').trim()
      if (trimmedSearch) {
        payload.search = trimmedSearch
      } else {
        // Explicitly omit search when cleared so payload does not keep a stale value
        delete payload.search
      }

      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const { attributes: list, pagination, sortBy: apiSortBy, sortOrder: apiSortOrder } =
        extractAttributesPayload(responseData)

      if (isApiSuccess(responseData)) {
        setAttributes(list)
        const total =
          typeof pagination.total === 'number'
            ? pagination.total
            : typeof pagination.totalCount === 'number'
              ? pagination.totalCount
              : list.length
        setTotalCount(total)
        if (apiSortBy) setSortBy(normalizeSortBy(apiSortBy))
        if (apiSortOrder) setSortOrder(normalizeSortOrder(apiSortOrder))
      } else {
        setAttributes([])
        setTotalCount(0)
        toast.error(responseData?.message || 'Failed to fetch attributes', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Fetch attributes error:', error)
      setAttributes([])
      setTotalCount(0)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(errData?.message || 'An error occurred while fetching attributes', {
        autoClose: 2000
      })
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, itemsPerPage, activeSearchQuery, sortBy, sortOrder])

  useEffect(() => {
    fetchAttributes()
  }, [fetchAttributes])

  useEffect(() => {
    sessionStorage.setItem(
      'attributeFilters',
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder
      })
    )
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder
  ])

  const hasActiveFilters = Boolean(String(activeSearchQuery || '').trim())
  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / itemsPerPage) || 1)
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalCount)

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)

    if (!String(value || '').trim()) {
      setActiveSearchQuery('')
      setCurrentPage(1)
    }
  }

  const handleSearchTrigger = () => {
    const trimmedQuery = String(searchQuery || '').trim()
    setCurrentPage(1)
    setActiveSearchQuery(trimmedQuery)
  }

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearchTrigger()
    }
  }

  const handleClearAll = () => {
    setSearchQuery('')
    setActiveSearchQuery('')
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page)
  }

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value) || 25)
    setCurrentPage(1)
  }

  const handleSort = (column) => {
    if (!ATTRIBUTE_SORT_FIELDS.includes(column)) return
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
    setCurrentPage(1)
  }

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <i className="fa-solid fa-sort ml-1" style={{ opacity: 0.35 }} />
    }
    return sortOrder === 'asc' ? (
      <i className="fa-solid fa-sort-up ml-1" />
    ) : (
      <i className="fa-solid fa-sort-down ml-1" />
    )
  }

  const renderSortableHeader = (column, label, style = {}) => (
    <th
      style={{ cursor: 'pointer', whiteSpace: 'nowrap', ...style }}
      onClick={() => handleSort(column)}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  )

  const getPageNumbers = () => {
    const pages = []
    const maxVisible = 5
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i)
      return pages
    }
    pages.push(1)
    let start = Math.max(2, currentPage - 1)
    let end = Math.min(totalPages - 1, currentPage + 1)
    if (currentPage <= 3) {
      start = 2
      end = 4
    }
    if (currentPage >= totalPages - 2) {
      start = totalPages - 3
      end = totalPages - 1
    }
    if (start > 2) pages.push('ellipsis')
    for (let i = start; i <= end; i += 1) pages.push(i)
    if (end < totalPages - 1) pages.push('ellipsis')
    pages.push(totalPages)
    return pages
  }

  const handleOpenAddAttributeModal = () => {
    setAddAttributeName('')
    openBootstrapModal('#modaladdattribute')
  }

  const handleCloseAddAttributeModal = () => {
    closeBootstrapModal('#modaladdattribute')
    setAddAttributeName('')
  }

  const handleCreateAttribute = async () => {
    const attribute = addAttributeName.trim()
    if (!attribute) {
      toast.error('Attribute is required', { autoClose: 2000 })
      return
    }

    const createdBy = localStorage.getItem('id')
    if (!createdBy) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsCreatingAttribute(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'create-attribute',
        attribute,
        createdBy
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || 'Attribute created successfully', {
          autoClose: 2000
        })
        handleCloseAddAttributeModal()
        if (currentPage !== 1) {
          setCurrentPage(1)
        } else {
          fetchAttributes()
        }
      } else {
        toast.error(responseData?.message || 'Failed to create attribute', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Create attribute error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while creating attribute',
        { autoClose: 2000 }
      )
    } finally {
      setIsCreatingAttribute(false)
    }
  }

  const handleCloseViewAttributeModal = () => {
    closeBootstrapModal('#modalviewattribute')
    setViewAttribute(null)
  }

  const handleViewClick = async (attribute) => {
    const id = getAttributeRecordId(attribute)
    if (!id) {
      toast.error('Attribute ID is missing', { autoClose: 2000 })
      return
    }

    setIsLoadingViewAttribute(true)
    setViewAttribute(null)
    openBootstrapModal('#modalviewattribute')

    try {
      const response = await axios.post(API_URL, {
        action: 'get-attribute-by-id',
        id
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const detail = extractAttributeDetail(responseData)

      if (isApiSuccess(responseData) && detail) {
        setViewAttribute(detail)
      } else {
        toast.error(responseData?.message || 'Failed to fetch attribute details', {
          autoClose: 2000
        })
        handleCloseViewAttributeModal()
      }
    } catch (error) {
      console.error('Fetch attribute error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while fetching attribute details',
        { autoClose: 2000 }
      )
      handleCloseViewAttributeModal()
    } finally {
      setIsLoadingViewAttribute(false)
    }
  }

  const handleCloseEditAttributeModal = () => {
    closeBootstrapModal('#modaleditattribute')
    setEditAttribute(null)
    setEditAttributeName('')
  }

  const handleEditClick = async (attribute) => {
    const id = getAttributeRecordId(attribute)
    if (!id) {
      toast.error('Attribute ID is missing', { autoClose: 2000 })
      return
    }

    setIsLoadingEditAttribute(true)
    setEditAttribute(null)
    setEditAttributeName('')
    openBootstrapModal('#modaleditattribute')

    try {
      const response = await axios.post(API_URL, {
        action: 'get-attribute-by-id',
        id
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const detail = extractAttributeDetail(responseData)

      if (isApiSuccess(responseData) && detail) {
        setEditAttribute(detail)
        setEditAttributeName(getAttributeLabel(detail))
      } else {
        toast.error(responseData?.message || 'Failed to fetch attribute details', {
          autoClose: 2000
        })
        handleCloseEditAttributeModal()
      }
    } catch (error) {
      console.error('Fetch attribute for edit error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while fetching attribute details',
        { autoClose: 2000 }
      )
      handleCloseEditAttributeModal()
    } finally {
      setIsLoadingEditAttribute(false)
    }
  }

  const handleUpdateAttribute = async () => {
    const id = getAttributeRecordId(editAttribute)
    const attribute = editAttributeName.trim()
    if (!id) {
      toast.error('Attribute ID is missing', { autoClose: 2000 })
      return
    }
    if (!attribute) {
      toast.error('Attribute is required', { autoClose: 2000 })
      return
    }

    const updatedBy = localStorage.getItem('id')
    if (!updatedBy) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsUpdatingAttribute(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'update-attribute',
        id,
        attribute,
        updatedBy
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || 'Attribute updated successfully', {
          autoClose: 2000
        })
        handleCloseEditAttributeModal()
        fetchAttributes()
      } else {
        toast.error(responseData?.message || 'Failed to update attribute', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Update attribute error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while updating attribute',
        { autoClose: 2000 }
      )
    } finally {
      setIsUpdatingAttribute(false)
    }
  }

  const handleDeleteClick = (attribute) => {
    const id = getAttributeRecordId(attribute)
    if (!id) {
      toast.error('Attribute ID is missing', { autoClose: 2000 })
      return
    }
    setDeleteAttributeId(id)
    setDeleteAttributeLabel(getAttributeLabel(attribute) || 'this attribute')
  }

  const handleDelete = async () => {
    if (!deleteAttributeId) return

    const updatedBy = localStorage.getItem('id')
    if (!updatedBy) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsDeleting(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'delete-attribute',
        id: deleteAttributeId,
        updatedBy
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || 'Attribute deleted successfully', {
          autoClose: 2000
        })
        closeBootstrapModal('#modaldeleteattribute')
        setDeleteAttributeId(null)
        setDeleteAttributeLabel('')
        if (attributes.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1)
        } else {
          fetchAttributes()
        }
      } else {
        toast.error(responseData?.message || 'Failed to delete attribute', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Delete attribute error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(errData?.message || 'An error occurred while deleting attribute', {
        autoClose: 2000
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Brand/OEM</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <button
                  type="button"
                  className="btn btn-blue"
                  onClick={handleOpenAddAttributeModal}
                >
                  Add Brand/OEM
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                  <div className="card supplier-stat-card">
                    <div className="card-body">
                      <div
                        className="stat-title"
                        style={{
                          color: '#196F3D',
                          fontSize: '13px',
                          fontWeight: '600',
                          marginBottom: '8px'
                        }}
                      >
                        Total Brand/OEM
                      </div>
                      <div
                        className="stat-value"
                        style={{
                          color: '#27AE60',
                          fontSize: '22px',
                          fontWeight: '700'
                        }}
                      >
                        {isLoading ? '—' : totalCount}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-3">
                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: '13px', fontWeight: '500' }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search attributes..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: 'pointer' }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : attributes.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no Brand/OEM yet.
                  <br />
                  {canCreate && (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handleOpenAddAttributeModal}
                      >
                        Add Brand/OEM
                      </button>
                    </div>
                  )}
                </div>
              ) : attributes.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No attributes found matching your search.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="attributes"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: 'nowrap',
                              width: '40px',
                              minWidth: '40px'
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader('attribute', 'Attribute', {
                            minWidth: '140px'
                          })}
                          <th style={{ minWidth: '140px' }}>Created By</th>
                          {renderSortableHeader('createdAt', 'Created At', {
                            minWidth: '140px'
                          })}
                          <th style={{ width: '100px', minWidth: '100px' }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {attributes.map((attribute, index) => {
                          const recordId = getAttributeRecordId(attribute)
                          return (
                            <tr key={recordId || index}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {getAttributeLabel(attribute) || '-'}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {attributeUserName(attribute.createdBy)}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {formatAttributeDate(attribute.createdAt)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: 'nowrap' }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  {canView && (
                                    <a
                                      href="#modalviewattribute"
                                      className="crsr_pntr action-icon-link mr-2"
                                      title="View"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        handleViewClick(attribute)
                                      }}
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </a>
                                  )}
                                  {canUpdate && (
                                    <a
                                      href="#modaleditattribute"
                                      className="crsr_pntr action-icon-link mr-2"
                                      title="Edit"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        handleEditClick(attribute)
                                      }}
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </a>
                                  )}
                                  {canDelete && (
                                    <a
                                      href="#modaldeleteattribute"
                                      className="crsr_pntr action-icon-link"
                                      title="Delete"
                                      data-toggle="modal"
                                      data-target="#modaldeleteattribute"
                                      onClick={() => handleDeleteClick(attribute)}
                                    >
                                      <i className="fa-solid fa-trash" />
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {totalCount > 0 && (
                    <div className="mt-3">
                      <div className="pagination-wrapper">
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: '13px' }}
                          >
                            Show:
                          </label>
                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <span className="ml-2" style={{ fontSize: '13px' }}>
                            entries
                          </span>
                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: '13px' }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${
                                currentPage === 1 ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentPage === 1 ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left" />
                              </button>
                            </li>
                            {getPageNumbers().map((page, index) =>
                              page === 'ellipsis' ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? 'active' : ''
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              )
                            )}
                            <li
                              className={`page-item ${
                                currentPage === totalPages ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentPage === totalPages ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalviewattribute"
        tabIndex={-1}
        aria-labelledby="modalviewattributeLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className="modal-content"
            style={{ border: 'none', borderRadius: '10px', overflow: 'hidden' }}
          >
            <div
              className="modal-header border-0"
              style={{ backgroundColor: '#1199B2', color: '#fff', padding: '16px 20px' }}
            >
              <h5 className="modal-title mb-0" id="modalviewattributeLabel">
                <i className="fa-solid fa-tag mr-2" />
                View Brand/OEM
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseViewAttributeModal}
                style={{ color: '#fff', opacity: 1, textShadow: 'none' }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', backgroundColor: '#fff' }}>
              {isLoadingViewAttribute ? (
                <div className="text-center py-5">
                  <PulseLoader color="#1199B2" size={10} />
                  <p className="text-muted small mt-3 mb-0">Loading Brand/OEM details...</p>
                </div>
              ) : viewAttribute ? (
                <>
                  <div
                    className="mb-4 p-3"
                    style={{
                      background: 'linear-gradient(135deg, #f0f9fb 0%, #e8f4f8 100%)',
                      border: '1px solid #cfe8ef',
                      borderRadius: '10px'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#1199B2',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        marginBottom: '6px'
                      }}
                    >
                      Brand/OEM
                    </div>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#17324d',
                        wordBreak: 'break-word'
                      }}
                    >
                      {getAttributeLabel(viewAttribute) || '-'}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-6 mb-3">
                      <AttributeDetailCard
                        label="Created By"
                        value={attributeUserName(viewAttribute.createdBy)}
                        icon="fa-solid fa-user"
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <AttributeDetailCard
                        label="Updated By"
                        value={attributeUserName(viewAttribute.updatedBy)}
                        icon="fa-solid fa-user-pen"
                      />
                    </div>
                    <div className="col-sm-6 mb-0">
                      <AttributeDetailCard
                        label="Created At"
                        value={formatAttributeDate(viewAttribute.createdAt)}
                        icon="fa-regular fa-calendar"
                      />
                    </div>
                    <div className="col-sm-6 mb-0">
                      <AttributeDetailCard
                        label="Updated At"
                        value={formatAttributeDate(viewAttribute.updatedAt)}
                        icon="fa-regular fa-clock"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  Brand/OEM details not available.
                </div>
              )}
            </div>
            <div
              className="modal-footer border-0 justify-content-center"
              style={{ backgroundColor: '#f8fafb', padding: '14px 20px' }}
            >
              <button
                type="button"
                className="btn btn-blue px-4"
                data-dismiss="modal"
                onClick={handleCloseViewAttributeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaladdattribute"
        tabIndex={-1}
        aria-labelledby="modaladdattributeLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className="modal-content"
            style={{ border: 'none', borderRadius: '10px', overflow: 'hidden' }}
          >
            <div
              className="modal-header border-0"
              style={{ backgroundColor: '#1199B2', color: '#fff', padding: '16px 20px' }}
            >
              <h5 className="modal-title mb-0" id="modaladdattributeLabel">
                <i className="fa-solid fa-plus mr-2" />
                Add Brand/OEM
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseAddAttributeModal}
                style={{ color: '#fff', opacity: 1, textShadow: 'none' }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', backgroundColor: '#fff' }}>
              <div className="form-group mb-0">
                <label>
                  Brand/OEM <span className="f_red">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Make / Brand / OEM"
                  value={addAttributeName}
                  onChange={(e) => setAddAttributeName(e.target.value)}
                  disabled={isCreatingAttribute}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleCreateAttribute()
                    }
                  }}
                />
              </div>
            </div>
            <div
              className="modal-footer border-0 justify-content-center"
              style={{ backgroundColor: '#f8fafb', padding: '14px 20px' }}
            >
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                onClick={handleCloseAddAttributeModal}
                disabled={isCreatingAttribute}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleCreateAttribute}
                disabled={isCreatingAttribute}
              >
                {isCreatingAttribute ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-1" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaleditattribute"
        tabIndex={-1}
        aria-labelledby="modaleditattributeLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className="modal-content"
            style={{ border: 'none', borderRadius: '10px', overflow: 'hidden' }}
          >
            <div
              className="modal-header border-0"
              style={{ backgroundColor: '#1199B2', color: '#fff', padding: '16px 20px' }}
            >
              <h5 className="modal-title mb-0" id="modaleditattributeLabel">
                <i className="fa-solid fa-pen mr-2" />
                Edit Brand/OEM
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseEditAttributeModal}
                style={{ color: '#fff', opacity: 1, textShadow: 'none' }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', backgroundColor: '#fff' }}>
              {isLoadingEditAttribute ? (
                <div className="text-center py-5">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : (
                <div className="form-group mb-0">
                  <label>
                    Brand/OEM <span className="f_red">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Make / Brand / OEM"
                    value={editAttributeName}
                    onChange={(e) => setEditAttributeName(e.target.value)}
                    disabled={isUpdatingAttribute || !editAttribute}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleUpdateAttribute()
                      }
                    }}
                  />
                </div>
              )}
            </div>
            <div
              className="modal-footer border-0 justify-content-center"
              style={{ backgroundColor: '#f8fafb', padding: '14px 20px' }}
            >
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                onClick={handleCloseEditAttributeModal}
                disabled={isUpdatingAttribute}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleUpdateAttribute}
                disabled={isUpdatingAttribute || isLoadingEditAttribute || !editAttribute}
              >
                {isUpdatingAttribute ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-1" />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldeleteattribute"
        tabIndex={-1}
        aria-labelledby="modaldeleteattributeLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteattributeLabel">
                Delete Brand/OEM
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete <strong>{deleteAttributeLabel}</strong>?
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button" 
                className="btn btn-secondary"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-1" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Attribute